<template>
	<EventLayout :event="event" :subtitle="trans('events.info.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="save">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<FileManager v-if="showFileManager" v-model:selected-file="event.image" @close="showFileManager = false"/>
		<div class="grid grid-cols-1 md:grid-cols-10 gap-3 lg:gap-6">
			<div class="md:col-start-2 md:col-span-8 lg:col-start-1 lg:col-span-4">
				<div class="relative hover:[&>:last-child]:flex cursor-pointer" @click.stop="showFileManager = true">
					<img v-if="event.image" class="object-cover w-full object-center bg-gray-100 dark:bg-gray-900 rounded-lg" :src="storage(event.image)" :alt="event.title ?? trans('events.title')">
					<div v-else class="w-full aspect-square bg-gray-100 dark:bg-gray-900 rounded-lg flex items-center justify-center">
						<i class="fa-solid fa-image fa-10x"/>
					</div>
					<div class="hidden absolute bottom-0 h-1/2 w-full px-3 py-5 flex-col gap-3 justify-end bg-gradient-to-b from-transparent to-gray-50/80 dark:to-gray-950/80">
						<i class="fa-solid fa-image"/>
						<p>{{ trans('general.upload_file') }}</p>
					</div>
				</div>
			</div>
			<div class="md:col-start-2 md:col-span-8 lg:col-start-5 lg:col-span-6 flex flex-col gap-3">
				<div class="grid grid-cols-2 gap-3">
					<LabeledObject v-if="event.is_joseii" class="col-span-2" full>
						<template #label>{{ trans('events.joseii.lemma') }}</template>
						<input v-model="event.lemma" type="text">
					</LabeledObject>
					<LabeledObject v-if="event.is_generic" class="col-span-2" full>
						<template #label>{{ trans('events.generic.name') }}</template>
						<input v-model="event.name" type="text">
					</LabeledObject>
					<template v-else>
						<LabeledObject full>
							<template #label>{{ trans('events.hosted.year') }}</template>
							<NumberInput v-model.number="event.year"/>
						</LabeledObject>
						<LabeledObject full>
							<template #label>{{ trans('events.hosted.edition') }}</template>
							<input v-model="event.edition" type="text">
						</LabeledObject>
					</template>
					<LabeledObject full>
						<template #label>{{ trans('events.begins_on') }}</template>
						<IconDateInput v-model="event.begins_on" icon-class="fa-solid fa-hourglass-start" full/>
					</LabeledObject>
					<LabeledObject full>
						<template #label>{{ trans('events.ends_on') }}</template>
						<IconDateInput v-model="event.ends_on" icon-class="fa-solid fa-hourglass-end" full/>
					</LabeledObject>
					<Switch v-model="event.active" :label="trans('general.active')"/>
					<Switch v-model="event.online" :label="trans('events.online.online')"/>
				</div>
				<Card :title="trans('events.host.title')" :subtitle="trans('events.host.subtitle')">
					<LabeledObject>
						<template #label>{{ trans('events.venue') }}</template>
						<IconTextInput v-model="event.venue" icon-class="fa-solid fa-location-dot" full/>
					</LabeledObject>
					<SearchLocation v-model:location-id="event.location_id" :location="event.location" :label="trans('events.location_id')" :countries="countries"/>
				</Card>
				<Card :title="trans('events.colleges.title')" :subtitle="trans('events.colleges.subtitle')">
					<SelectColleges :colleges="event.colleges" :event-id="event.id" manage/>
				</Card>
				<Card :title="trans('events.social_networks.title')" :subtitle="trans('events.social_networks.subtitle')">
					<LabeledObject>
						<template #label>{{ trans('events.instagram') }}</template>
						<IconTextInput v-model="event.instagram" icon-class="fa-brands fa-instagram" full/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.twitter') }}</template>
						<IconTextInput v-model="event.twitter" icon-class="fa-brands fa-x-twitter" full/>
					</LabeledObject>
				</Card>
			</div>
		</div>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {type Country} from '@/Types/Country'
import {type Location} from '@/Types/Location'
import {defineProps, ref, type Ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {type Event} from '@/Types/Events/Event'
import Switch from '@/Components/UI/Switches/Switch.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import FileManager from '@/Components/Files/FileManager.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import IconTextInput from '@/Components/UI/Inputs/IconTextInput.vue'
import IconDateInput from '@/Components/UI/Inputs/IconDateInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SearchLocation from '@/Components/Search/SearchLocation.vue'
import SelectColleges from '@/Components/Search/SelectColleges.vue'
import {showInfo, showSuccess, showValidationErrors, storage} from '@/Helpers/general'

const props = defineProps<{
	event:Event,
	location:Location,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const showFileManager:Ref<boolean> = ref(false)
const countries:Ref<Array<Country>> = ref([
	{
		id: 7,
		image_url: 'https://upload.wikimedia.org/wikipedia/commons/1/1a/Flag_of_Argentina.svg',
		en_name: 'Argentina',
		es_name: 'Argentina',
	},
] as Array<Country>)

function save():void{
	if(!working.value && event.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.update', event.value.id), event.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>
